import { Typography } from '@mui/material';
import React from 'react';

import TeamIcon from '@sbu/assets.svg/src/team.svg';
import UnassignedIcon from '@sbu/assets.svg/src/unassigned.svg';
import { GraphqlContextValues } from '@sbu/contexts.graphql';

import { flattenResolutions } from 'app/Config/InsightResolutions';
import { TRIAGE_VERDICT_LABELS } from 'app/Insights/InsightAI/types';
import {
  InsightResolutionFragment as InsightResolutionType,
  SearchResultType,
  TriageVerdict,
} from 'core/graphql';
import { Avatar } from 'shared/components/elements/Avatar';

import css from '../SearchFilter.module.scss';
import { FieldTypes, keyValueFieldDefaults } from '../types/fieldTypes';
import {
  dateOperators,
  dateRangeOperators,
  globalOperators,
  keyValueOperators,
  numericOperators,
  stringOperators,
  textOperators,
} from '../types/operatorTypes';
import { getEntityTypeFilters } from '../utils';
import { autocompleteQuery } from './gql';

export interface UserType {
  username: string;
  displayName: string;
  email?: string | null;
}

export interface TeamType {
  name: string;
}

export interface InsightStatusType {
  id: string;
  name: string;
  displayName: string;
}

export const getInsightFields = (
  graphqlContext: GraphqlContextValues,
  statuses?: Array<InsightStatusType>,
  users: UserType[] = [],
  teams: TeamType[] = [],
  resolutions: InsightResolutionType[] = [],
  canSearchCanaries = false,
  isAgenticSecOpsEnabled = false
): FieldTypes[] => [
  {
    label: 'Status',
    prop: 'status',
    operators: [...globalOperators],
    type: 'list',
    source: 'insight',
    options: (statuses || []).map((status) => ({
      prop: status.name,
      label: status.displayName,
    })),
  },
  {
    label: 'Assignee',
    prop: 'assignee',
    operators: [...globalOperators],
    type: 'list',
    source: 'insight',
    options: [
      {
        prop: null,
        label: 'Unassigned',
        listTemplate: (label) => (
          <div>
            <UnassignedIcon className={css.unassignedIcon} />
            <Typography component='label' sx={{ verticalAlign: 'middle' }}>
              {label}
            </Typography>
          </div>
        ),
      },
      ...[...users]
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((user) => ({
          prop: user.username,
          label: user.displayName,
          group: 'Users',
          listTemplate: (label) => (
            <div>
              <Avatar
                className={css.listAvatar}
                email={user.email}
                size='small'
              />
              <Typography component='label' sx={{ verticalAlign: 'middle' }}>
                {label}
              </Typography>
            </div>
          ),
        })),
      ...[...teams]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((team) => ({
          prop: team.name,
          label: team.name,
          group: 'Teams',
          listTemplate: (label) => (
            <div>
              <TeamIcon className={css.unassignedIcon} />
              <Typography component='label' sx={{ verticalAlign: 'middle' }}>
                {label}
              </Typography>
            </div>
          ),
        })),
    ],
  },
  {
    label: 'Created',
    prop: 'created',
    operators: [
      ...globalOperators,
      ...dateRangeOperators.filter((d) => d.type !== 'next'),
      ...dateOperators,
    ],
    type: 'date',
    source: 'insight',
    disableFuture: true,
  },
  {
    label: 'Event Time',
    prop: 'timestamp',
    operators: [
      ...globalOperators,
      ...dateRangeOperators.filter((d) => d.type !== 'next'),
      ...dateOperators,
    ],
    type: 'date',
    source: 'insight',
    disableFuture: true,
  },
  {
    label: 'Severity',
    prop: 'severity',
    operators: [...globalOperators, ...numericOperators],
    type: 'list',
    source: 'insight',
    options: ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((v) => ({
      prop: v,
      label: v,
    })),
  },
  ...(isAgenticSecOpsEnabled
    ? [
        {
          label: 'AI Verdict',
          prop: 'triageVerdict',
          operators: [...globalOperators],
          type: 'list',
          source: 'insight',
          options: [
            {
              prop: TriageVerdict.Malicious,
              label: TRIAGE_VERDICT_LABELS[TriageVerdict.Malicious],
            },
            {
              prop: TriageVerdict.Suspicious,
              label: TRIAGE_VERDICT_LABELS[TriageVerdict.Suspicious],
            },
            {
              prop: TriageVerdict.Benign,
              label: TRIAGE_VERDICT_LABELS[TriageVerdict.Benign],
            },
            {
              prop: TriageVerdict.InProgress,
              label: TRIAGE_VERDICT_LABELS[TriageVerdict.InProgress],
            },
            {
              prop: TriageVerdict.Inconclusive,
              label: TRIAGE_VERDICT_LABELS[TriageVerdict.Inconclusive],
            },
          ],
        } as FieldTypes,
      ]
    : []),
  {
    label: 'Resolution',
    prop: 'resolution',
    operators: [...globalOperators],
    type: 'list',
    source: 'insight',
    options: resolutions.map(({ name }) => ({
      prop: name,
      label: name,
    })),
  },
  {
    label: 'Custom Resolution',
    prop: 'subResolution',
    operators: [...globalOperators],
    type: 'list',
    source: 'insight',
    options: flattenResolutions(resolutions)
      .map(({ name, parentName }) => ({
        prop: name,
        label: name,
        group: parentName,
      }))
      .filter(({ group }) => !!group),
  },
  {
    label: 'Name',
    prop: 'name',
    operators: [...globalOperators, ...textOperators, ...stringOperators],
    type: 'text',
    source: 'insight',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Insights,
        'name',
        value,
        graphqlContext
      ),
  },
  {
    ...keyValueFieldDefaults,
    label: 'Tags',
    prop: 'tag',
    operators: [...keyValueOperators],
    source: 'insight',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Insights,
        'tag',
        value,
        graphqlContext
      ),
  },
  {
    label: 'Rule ID',
    prop: 'ruleId',
    operators: [...globalOperators],
    type: 'text',
    source: 'insight',
  },
  {
    label: 'Sensor Zone (Record)',
    prop: 'sensorZone',
    operators: [...globalOperators],
    type: 'text',
    source: 'insight',
  },
  {
    label: 'Entity Value',
    prop: 'entity.value',
    operators: [...globalOperators, ...stringOperators],
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Entities,
        'value',
        value,
        graphqlContext
      ),
    type: 'text',
    source: 'insight',
  },
  {
    label: 'Entity Type',
    prop: 'entity.type',
    operators: [...globalOperators],
    type: 'list',
    source: 'insight',
    options: getEntityTypeFilters(),
  },
  {
    label: 'Entity Sensor Zone',
    prop: 'entity.sensorZone',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Entities,
        'sensorZone',
        value,
        graphqlContext
      ),
    operators: [...globalOperators],
    type: 'text',
    source: 'insight',
  },
  ...(canSearchCanaries
    ? [
        {
          label: 'Canaries',
          prop: 'canary',
          operators: [
            {
              label: 'should be',
              type: 'equals',
            },
          ],
          type: 'list',
          source: 'insight',
          options: [
            {
              label: 'Excluded (default)',
              prop: 'exclude',
            },
            {
              label: 'Included (along with non-canaries)',
              prop: 'include',
            },
            {
              label: 'Included (without non-canaries)',
              prop: 'only',
            },
          ],
        } as FieldTypes,
      ]
    : []),
];
